import { ChangeEvent, Dispatch, useCallback, useState } from 'react';
import { useDispatch } from 'react-redux';

import { Button } from 'UIPackage/MainModule/components/Button';
import { isFileExtensionValid } from 'CorePackage/MainModule/validators/isFileExtensionValid';
import { Modal } from 'UIPackage/ModalModule/components/Modal';
import { ModalHeader } from 'UIPackage/ModalModule/components/ModalHeader';
import { ModalBody } from 'UIPackage/ModalModule/components/ModalBody';
import { ModalActions } from 'UIPackage/ModalModule/components/ModalActions';
import { ISignSigSubjectInfoCheckResultResponse } from 'APIPackage/KnmModule/interfaces/ISignSubjectInfo';
import { addNoticeAction } from 'CorePackage/NotificationModule/actions/NoticeActions';
import { NoticeTypesEnum } from 'CorePackage/NotificationModule/enums/NoticeTypesEnum';
import { MessageTypesEnum } from 'CorePackage/NotificationModule/enums/MessageTypesEnum';
import { RootActions } from 'CorePackage/MainModule/types/RootActions';

import styles from './SignatureImportModal.module.scss';

const ERROR_EXT = 'Недопустимый формат файла';
const validExtensions = ['sig'];

interface ISignatureImportModalProps {
  visible: boolean;
  isEnableKnoValidationSigning: boolean;
  onSend: () => void;
  onClose: () => void;
  uploadFunc: (file: File, id: number) => Promise<ISignSigSubjectInfoCheckResultResponse | boolean>;
  id?: number;
}

export const SignatureImportModal = ({
  visible,
  isEnableKnoValidationSigning,
  onSend,
  onClose,
  id,
  uploadFunc,
}: ISignatureImportModalProps) => {
  const [error, setError] = useState<string | undefined>(undefined);
  const [sigFile, setFile] = useState<File | undefined>(undefined);
  const dispatch = useDispatch<Dispatch<RootActions>>();

  const send = useCallback(async () => {
    let hasError = false;

    try {
      if (sigFile && id) {
        const results = await uploadFunc(sigFile, id);

        if (typeof results !== 'boolean') {
          if (results?.checkResult?.orgNameError) {
            if (isEnableKnoValidationSigning) {
              dispatch(
                addNoticeAction({
                  renderContent: results.checkResult.orgNameError,
                  noticeType: NoticeTypesEnum.ERROR,
                  messageType: MessageTypesEnum.VALIDATION_PM_SIGN,
                }),
              );
              hasError = true;
            } else {
              dispatch(
                addNoticeAction({
                  renderContent: results.checkResult.orgNameError,
                  noticeType: NoticeTypesEnum.WARNING,
                  messageType: MessageTypesEnum.VALIDATION_PM_SIGN,
                }),
              );
            }
          }

          if (
            !results.result &&
            results.checkResult?.fieldsCheckResult &&
            results.checkResult.fieldsCheckResult.length > 0
          ) {
            results.checkResult.fieldsCheckResult.forEach(checkErrorMessage => {
              dispatch(
                addNoticeAction({
                  renderContent: checkErrorMessage,
                  noticeType: NoticeTypesEnum.ERROR,
                  messageType: MessageTypesEnum.VALIDATION_PM_SIGN,
                }),
              );
            });
            hasError = true;
          }
        }
      }
      if (!hasError) onSend();
    } catch (err) {
      //
    }
  }, [id, onSend, sigFile]);

  const handleFileChange = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    e.preventDefault();
    const targetFiles = e.target.files;

    if (!targetFiles) {
      return;
    }
    const file = targetFiles[0];
    const fileName = file.name;

    if (!isFileExtensionValid(fileName, validExtensions)) {
      setError(ERROR_EXT);

      return;
    }
    setError(undefined);
    setFile(file);
    e.target.value = '';
  }, []);

  const onRemove = useCallback(() => setFile(undefined), []);

  if (!visible) {
    return null;
  }

  return (
    <Modal visible={visible} onClose={onClose} modalBodyClassName={styles.Body}>
      <ModalHeader title="Импорт подписи" />
      <ModalBody size="content" border="bottom">
        <div className={styles.LoadBlock}>
          <label className={styles.LoadButton}>
            Выбрать
            <input className={styles.Input} type="file" name="load" onChange={handleFileChange} />
          </label>
        </div>
        <div className={styles.FileBlock}>
          {sigFile && (
            <span className={styles.NameBlock}>
              <span className={styles.FileName}>{sigFile?.name}</span>
              <>
                &nbsp;
                <button className={styles.RemoveButton} type="button" onClick={onRemove}>
                  x
                </button>
              </>
            </span>
          )}
          {error && <div className={styles.Error}>{error}</div>}
        </div>
      </ModalBody>
      <ModalActions>
        <Button text="Отправить" color="primary" size="medium" disabled={!sigFile} onClick={send} />
        <Button text="Закрыть" size="medium" onClick={onClose} />
      </ModalActions>
    </Modal>
  );
};
